import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GetReportingTimeFormat,
  LocalTZtoTimezone,
} from '@core/constants/time.defaults';
import { Store } from '@ngrx/store';
import {
  FromDateSelector,
  SetSystemDatesAction,
  SetToDateIntervalAction,
  TimezoneSelector,
  ToDateSelector,
} from '@dashboard/dashboard-store';
import { TimezonesService } from '@dashboard/components/timezones/timezones.service';
import {
  DefaultFromDateRouteParam,
  DefaultToDateRouteParam,
} from '@dashboard/dashboard-store/reducers/ui-state.initial';
import { RouteIds } from '@dashboard/dynamic-dashboards.route';
import { combineLatest, firstValueFrom, take } from 'rxjs';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class DashboardDateSyncService {
  private store = inject(Store);
  private router = inject(Router);
  private timezonesService = inject(TimezonesService);

  async setDateRangeFromPicker(
    dates: { from: Date; to: Date },
    options?: { syncRoute?: boolean; route?: ActivatedRoute }
  ) {
    const currentToDate = await firstValueFrom(
      this.store.select(ToDateSelector)
    );
    const tz = await firstValueFrom(this.store.select(TimezoneSelector));
    const tzString = this.timezonesService.getTimezoneByHour(tz).valueStr;
    const isToDateSame = currentToDate === GetReportingTimeFormat(dates.to);

    const payloadDates: { fromDate: string; toDate?: string } = {
      fromDate: LocalTZtoTimezone(dates.from, tzString),
      toDate: LocalTZtoTimezone(dates.to, tzString),
    };

    if (options?.syncRoute && options.route) {
      this.syncDateQueryParams(options.route, {
        to: payloadDates.toDate,
        from: payloadDates.fromDate,
      });
    }

    if (isToDateSame) delete payloadDates.toDate;

    this.store.dispatch(SetSystemDatesAction(payloadDates));
  }

  setDateRangeFromRouteParams(dates: { to: string; from?: string }) {
    this.store.dispatch(
      SetSystemDatesAction({
        toDate: dates.to,
        ...(dates.from ? { fromDate: dates.from } : {}),
      })
    );
  }

  syncDateRangeFromNavigation(route: ActivatedRoute): void {
    const query = this.router.routerState.snapshot.root.queryParamMap;
    const hasVesselRoute = this.hasVesselRouteInCurrentTree();

    combineLatest([
      this.store.select(FromDateSelector),
      this.store.select(ToDateSelector),
    ])
      .pipe(take(1))
      .subscribe(([currentFrom, currentTo]) => {
        const to = query.get('to') ?? currentTo ?? DefaultToDateRouteParam();
        let from =
          (hasVesselRoute ? query.get('from') : null) ??
          currentFrom ??
          DefaultFromDateRouteParam(to);

        // A historical URL with no start date still needs a valid range.
        if (
          (!hasVesselRoute || !query.get('from')) &&
          moment.utc(from).isAfter(moment.utc(to))
        ) {
          from = DefaultFromDateRouteParam(to);
        }

        const toChanged = !moment.utc(to).isSame(moment.utc(currentTo));
        const fromChanged = !moment.utc(from).isSame(moment.utc(currentFrom));
        if (toChanged || fromChanged) {
          this.setDateRangeFromRouteParams({ to, from });
        }

        const urlTo = moment.utc(to).format('YYYY-MM-DDTHH:mm:ss');
        const urlFrom = hasVesselRoute
          ? moment.utc(from).format('YYYY-MM-DDTHH:mm:ss')
          : null;
        if (query.get('to') !== urlTo || query.get('from') !== urlFrom) {
          this.syncDateQueryParams(route, { to, from });
        }
      });
  }

  async setToDateInterval(
    toDate: Date,
    options?: { syncRoute?: boolean; route?: ActivatedRoute }
  ) {
    const fromDate = await firstValueFrom(this.store.select(FromDateSelector));
    const tz = await firstValueFrom(this.store.select(TimezoneSelector));
    const tzString = this.timezonesService.getTimezoneByHour(tz).valueStr;
    const normalizedToDate = LocalTZtoTimezone(toDate, tzString);

    if (options?.syncRoute && options.route) {
      this.syncDateQueryParams(options.route, {
        to: normalizedToDate,
        from: fromDate,
      });
    }
    this.store.dispatch(
      SetToDateIntervalAction({ toDate: normalizedToDate, updateState: false })
    );
  }

  syncDateQueryParams(
    route: ActivatedRoute,
    dates: { to: string; from?: string }
  ) {
    const queryParams: { to: string; from?: string | null } = {
      to: moment.utc(dates.to).format('YYYY-MM-DDTHH:mm:ss'),
    };

    if (this.hasVesselRouteInCurrentTree()) {
      queryParams.from = moment
        .utc(dates.from ?? DefaultFromDateRouteParam(dates.to))
        .format('YYYY-MM-DDTHH:mm:ss');
    } else {
      queryParams.from = null;
    }

    this.router.navigate([], {
      relativeTo: route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private hasVesselRouteInCurrentTree() {
    let route = this.router.routerState.snapshot.root;

    while (route) {
      if (route.routeConfig?.path === `:${RouteIds.vesselImo}`) {
        return true;
      }
      route = route.firstChild;
    }

    return false;
  }
}
